import React from 'react'
import styled from 'styled-components'
import { Icon } from '../../atoms/Icon'
import { ToggleSwitch } from '../../atoms/ToggleSwitch'

// Search Input
const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 14px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all ${({ theme }) => theme.transitions.default};

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.background.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}1A;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

const SearchIconWrapper = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.tertiary};
  pointer-events: none;
`

export const FilterSearch: React.FC<{
  placeholder: string
  value: string
  onChange: (value: string) => void
}> = ({ placeholder, value, onChange }) => (
  <SearchWrapper>
    <SearchInput
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    <SearchIconWrapper>
      <Icon name="search" size="small" />
    </SearchIconWrapper>
  </SearchWrapper>
)

// Select Dropdown
const SelectWrapper = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

const SelectInput = styled.select<{ $hasValue?: boolean }>`
  width: 100%;
  padding: 10px 36px 10px 12px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $hasValue }) =>
      $hasValue ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 13px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  appearance: none;

  &:hover {
    border-color: ${({ theme, $hasValue }) =>
      $hasValue ? theme.colors.primaryHover : theme.colors.text.tertiary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}1A;
  }
`

const SelectArrow = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const FilterSelect: React.FC<{
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
}> = ({ value, onChange, options, placeholder = 'Select...' }) => (
  <SelectWrapper>
    <SelectInput
      value={value}
      onChange={(e) => onChange(e.target.value)}
      $hasValue={!!value}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </SelectInput>
    <SelectArrow>
      <Icon name="arrow-down" size="small" />
    </SelectArrow>
  </SelectWrapper>
)

// Input Group
const InputGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

const InputWrapper = styled.div`
  flex: 1;
  position: relative;
`

const InputLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

const InputField = styled.input<{ $hasValue?: boolean }>`
  width: 100%;
  padding: 8px 0px 8px 12px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $hasValue }) =>
      $hasValue ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 13px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme, $hasValue }) =>
      $hasValue ? theme.colors.primaryHover : theme.colors.border.focus};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}1A;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

const InputSuffix = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  pointer-events: none;
`

export const FilterInputGroup: React.FC<{
  inputs: Array<{
    label?: string
    placeholder: string
    value: string
    onChange: (value: string) => void
    suffix?: string
    type?: string
    step?: string | number
  }>
}> = ({ inputs }) => (
  <InputGroup>
    {inputs.map((input, index) => (
      <InputWrapper key={index}>
        {input.label && <InputLabel>{input.label}</InputLabel>}
        <InputField
          type={input.type || 'text'}
          placeholder={input.placeholder}
          value={input.value}
          onChange={(e) => input.onChange(e.target.value)}
          step={input.step}
          $hasValue={!!input.value}
        />
        {input.suffix && <InputSuffix>{input.suffix}</InputSuffix>}
      </InputWrapper>
    ))}
  </InputGroup>
)

// Radio Group
const RadioGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-wrap: wrap;
`

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  user-select: none;
  font-size: 12px;
`

const RadioInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`

const RadioBox = styled.span<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border: 2px solid
    ${({ checked, theme }) =>
      checked ? theme.colors.primary : theme.colors.border.default};
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.spacing.xsmall};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ checked, theme }) =>
      checked ? theme.colors.primary : theme.colors.text.tertiary};
  }

  ${({ checked, theme }) =>
    checked &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      background: ${theme.colors.primary};
      border-radius: 50%;
    }
  `}
`

const RadioLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`

export const FilterRadioGroup: React.FC<{
  name: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
}> = ({ name, value, onChange, options }) => (
  <RadioGroup>
    {options.map((option) => (
      <RadioItem key={option.value}>
        <RadioInput
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={(e) => onChange(e.target.value)}
        />
        <RadioBox checked={value === option.value} />
        <RadioLabel>{option.label}</RadioLabel>
      </RadioItem>
    ))}
  </RadioGroup>
)

// Checkbox Group
const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  user-select: none;
  font-size: 13px;
  padding: ${({ theme }) => theme.spacing.xsmall} 0;
`

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`

const CheckboxBox = styled.span<{ checked: boolean }>`
  width: 18px;
  height: 18px;
  border: 2px solid
    ${({ checked, theme }) =>
      checked ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-right: ${({ theme }) => theme.spacing.small};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.default};
  background: ${({ checked, theme }) =>
    checked ? theme.colors.primary : 'transparent'};

  &:hover {
    border-color: ${({ checked, theme }) =>
      checked ? theme.colors.primary : theme.colors.text.tertiary};
  }

  ${({ checked }) =>
    checked &&
    `
    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  `}
`

const CheckboxLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`

export const FilterCheckboxGroup: React.FC<{
  options: Array<{
    value: string
    label: string
    checked: boolean
    onChange: (checked: boolean) => void
  }>
}> = ({ options }) => (
  <CheckboxGroup>
    {options.map((option) => (
      <CheckboxItem key={option.value}>
        <CheckboxInput
          type="checkbox"
          checked={option.checked}
          onChange={(e) => option.onChange(e.target.checked)}
        />
        <CheckboxBox checked={option.checked} />
        <CheckboxLabel>{option.label}</CheckboxLabel>
      </CheckboxItem>
    ))}
  </CheckboxGroup>
)

// Zone Type Label
export const ZoneTypeLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

// Zone Type Group
export const ZoneTypeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`

// Filter Subsection
export const FilterSubsection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  &:last-child {
    margin-bottom: 0;
  }
`

// Field Label
export const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

// Field Label with Tooltip
const FieldLabelWrapper = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const FieldLabelInner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const FieldLabelText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`

const InfoIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: help;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 173, 230, 0.1);
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 10px;
    height: 10px;
  }
`

const TooltipBox = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.98);
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 400;
  white-space: pre-line;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  transform: translateY(${({ $isVisible }) => ($isVisible ? '0' : '-4px')});
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
`

export const FieldLabelWithTooltip: React.FC<{
  label: string
  tooltip?: string
}> = ({ label, tooltip }) => {
  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsTooltipVisible(true)
    }, 100)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsTooltipVisible(false)
  }

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!tooltip) {
    return <FieldLabel>{label}</FieldLabel>
  }

  return (
    <FieldLabelWrapper>
      <FieldLabelInner>
        <FieldLabelText>{label}</FieldLabelText>
        <InfoIconWrapper
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Icon name="info" size="xs" />
        </InfoIconWrapper>
      </FieldLabelInner>
      <TooltipBox $isVisible={isTooltipVisible}>{tooltip}</TooltipBox>
    </FieldLabelWrapper>
  )
}

// Single Checkbox
const SingleCheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  padding: ${({ theme }) => theme.spacing.xsmall} 0;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

const SingleCheckboxInput = styled.input`
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`

export const FilterCheckbox: React.FC<{
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
}> = ({ checked, onChange, label }) => (
  <SingleCheckboxWrapper>
    <SingleCheckboxInput
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    {label}
  </SingleCheckboxWrapper>
)

// Checkbox Container
export const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

// Toggle Item
const ToggleItemWrapper = styled.label`
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.medium};
  cursor: pointer;
  user-select: none;
`

const ToggleLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-left: ${({ theme }) => theme.spacing.small};
`

export const FilterToggle: React.FC<{
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  disabled?: boolean
}> = ({ checked, onChange, label, disabled }) => (
  <ToggleItemWrapper>
    <ToggleSwitch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      size="medium"
    />
    <ToggleLabel>{label}</ToggleLabel>
  </ToggleItemWrapper>
)
